'use client';

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';
import useSWR from 'swr';
import Link from 'next/link';
import { useSocialLinks, findSocialLink } from '@/hooks/useSocialLinks';
import { formatSocialHref, formatSocialDisplayText } from '@/utils/socialLink';

// Components
import Hero from '@/components/Hero';
import FeaturesSection from '@/components/FeaturesSection';
import KittenPreview from '@/components/KittenPreview';
import GalleryPreview from '@/components/GalleryPreview';
import TestimonialsSection from '@/components/TestimonialsSection';
import FaqPreview from '@/components/FaqPreview';
import CallToAction from '@/components/CallToAction';
import SafetyAssurance from '@/components/SafetyAssurance';

const API = '/api';
const fetcher = (url: string) => fetch(url).then((res) => res.json());

// Fallback testimonials data
const fallbackTestimonials = [
  {
    name: 'Amelia Rhodes',
    message: 'Our kitten, Lumi, is a dream. She transitioned into our home with confidence and grace thanks to the socialization LINDY provided.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=200&q=80',
    text: 'Lumi has brought so much joy to our lives. Thank you, LINDY!',
    author: 'Amelia Rhodes'
  },
  {
    name: 'Daniel Patel',
    message: 'Every interaction felt bespoke. From the first consultation to follow-up care, LINDY Balinese Cats exceeded every expectation.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1487412912498-0447578fcca8?auto=format&fit=crop&w=200&q=80',
    text: 'The care and attention to detail were unmatched. Highly recommend!',
    author: 'Daniel Patel'
  },
  {
    name: 'Gabriella Moreno',
    message: 'The process was transparent and thoughtful. We felt supported throughout, and our kitten is healthy, loving, and stunning.',
    rating: 5,
    image: 'https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?auto=format&fit=crop&w=200&q=80',
    text: 'Our kitten is the perfect addition to our family. Thank you!',
    author: 'Gabriella Moreno'
  }
];

export default function Home() {
  // State for testimonials carousel
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const { links: socialLinks } = useSocialLinks();

  // Fetch data
  const { data: kittenData, isLoading: kittensLoading } = useSWR(
    `${API}/kittens`,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  const { data: galleryData, isLoading: galleryLoading } = useSWR(
    `${API}/gallery`,
    fetcher,
    { refreshInterval: 0, revalidateOnFocus: false }
  );

  // Auto-advance testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % fallbackTestimonials.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  // Swipe handlers for testimonials
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => setCurrentTestimonial((prev) => (prev + 1) % fallbackTestimonials.length),
    onSwipedRight: () => setCurrentTestimonial((prev) => (prev - 1 + fallbackTestimonials.length) % fallbackTestimonials.length)
  });

  // Process data with fallbacks
  const kittensPreview = Array.isArray(kittenData)
    ? kittenData.slice(0, 3).map(kitten => ({
      id: kitten.id,
      name: kitten.name,
      description: kitten.personality_notes || `Beautiful ${kitten.color || 'Balinese'} kitten`,
      color: kitten.color || 'Balinese',
      sex: kitten.gender === 'male' ? 'Sire' : kitten.gender === 'female' ? 'Queen' : 'Kitten',
      age: kitten.age || 'Recently Born',
      heroImage: kitten.main_photo || (kitten.photos && kitten.photos.length > 0 ? kitten.photos[0] : ''),
      images: kitten.photos || []
    }))
    : [];
  const galleryPreview = Array.isArray(galleryData) ? galleryData.slice(0, 6) : [];

  const handleTestimonialNavigation = (index: number) => {
    setCurrentTestimonial(index);
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const facebookGroup = useMemo(() => {
    const link = findSocialLink(socialLinks, 'FACEBOOK_GROUP');
    return {
      href: link ? formatSocialHref(link) : 'https://www.facebook.com/groups/797522661874602',
      label: link ? formatSocialDisplayText(link, 'Join our Facebook group') : 'Join our Facebook group',
    };
  }, [socialLinks]);

  return (
    <main className="min-h-screen">
      <Hero />
      <FeaturesSection />
      <KittenPreview kittens={kittensPreview} />
      <GalleryPreview items={galleryPreview} />
      <TestimonialsSection
        testimonials={fallbackTestimonials}
        currentTestimonial={currentTestimonial}
        onTestimonialChange={handleTestimonialNavigation}
        swipeHandlers={swipeHandlers}
        showCards={false}
      />
      <section className="px-6 py-16">
        <div className="max-w-6xl mx-auto">
          <FaqPreview />
        </div>
      </section>
      <motion.section
        className="px-6 pb-16"
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <div className="relative max-w-6xl mx-auto overflow-hidden rounded-3xl bg-gradient-to-br from-[#1877F2]/15 via-white to-cream shadow-[0_28px_80px_rgba(24,119,242,0.22)]">
          <motion.div
            className="pointer-events-none absolute -top-20 -left-24 h-64 w-64 rounded-full bg-[#1877F2]/40 blur-3xl"
            animate={{ opacity: [0.25, 0.45, 0.25], scale: [0.9, 1.04, 0.9] }}
            transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="pointer-events-none absolute bottom-[-6rem] right-[-4rem] h-72 w-72 rounded-full bg-sky-200/40 blur-[140px]"
            animate={{ opacity: [0.2, 0.36, 0.2], scale: [0.85, 1.1, 0.9] }}
            transition={{ duration: 16, repeat: Infinity, ease: 'easeInOut', delay: 1.1 }}
          />

          <div className="relative z-10 grid gap-10 p-10 md:grid-cols-[1.15fr,0.85fr] items-center">
            <div className="space-y-6">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#1877F2]/30 bg-white/80 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-[#1877F2]">
                Facebook Salon • Daily Stories
              </span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-warm-brown">
                Join our private Facebook lounge
              </h2>
              <p className="text-lg text-warm-brown/75 max-w-2xl">
                Meet fellow adopters, watch behind-the-scenes nursery lives, and receive priority alerts when new Balinese pairings open. Our community shares enrichment tips, wellness rituals, and wins from around the world.
              </p>
              <ul className="grid gap-3 sm:grid-cols-2 text-sm text-warm-brown/70">
                {[
                  'Weekly live Q&A with breeders',
                  'Exclusive growth journals & reels',
                  'First access to upcoming litters',
                  'Peer support from LINDY families'
                ].map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#1877F2]/15 text-[#1877F2]">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col sm:flex-row gap-4">
                <motion.a
                  href={facebookGroup.href}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center rounded-xl bg-[#1877F2] px-6 py-3 text-base font-semibold text-white shadow-lg transition-transform duration-300 hover:-translate-y-0.5 hover:shadow-xl"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.96 }}
                >
                  {facebookGroup.label}
                </motion.a>
                <Link
                  href="/gallery"
                  className="inline-flex items-center justify-center rounded-xl border border-[#1877F2]/40 px-6 py-3 text-base font-semibold text-[#1877F2] transition-colors duration-300 hover:bg-[#1877F2]/90 hover:text-white"
                >
                  Preview community moments
                </Link>
              </div>
            </div>

            <motion.div
              className="relative rounded-3xl border border-[#1877F2]/30 bg-white/80 p-6 shadow-[0_20px_60px_rgba(24,119,242,0.18)] backdrop-blur"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1877F2]/10 text-[#1877F2] text-lg">💬</span>
                <div>
                  <p className="text-xs uppercase tracking-[0.25em] text-[#1877F2]">Community pulse</p>
                  <p className="text-warm-brown font-semibold">1,200+ members sharing daily inspiration</p>
                </div>
              </div>
              <div className="space-y-4 text-sm text-warm-brown/75">
                <div className="rounded-2xl bg-[#1877F2]/10 p-4">
                  <p className="font-semibold text-warm-brown">Today’s spotlight</p>
                  <p>See Luna’s enrichment maze and the calming playlist we use for nest time.</p>
                </div>
                <div className="rounded-2xl bg-[#1877F2]/10 p-4">
                  <p className="font-semibold text-warm-brown">Evening salon</p>
                  <p>Live walkthrough every Sunday at 6PM PST with curated temperament notes.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </motion.section>
      <SafetyAssurance />
      <CallToAction />
    </main>
  );
}
